import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Stack,
    Typography,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Edit as EditIcon,
    Autorenew as AutorenewIcon,
    DeleteOutline,
    ContentCopy,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';

import WrapperSection from '../../components/global/WrapperSection';
import GlobalBreadcrumbs from '../../components/global/GlobalBreadcrumbs';
import WebhookDeliveriesTable from '../../components/webhooks/WebhookDeliveriesTable';
import WebhookSecretDialog from '../../components/webhooks/WebhookSecretDialog';
import RotateSecretDialog from '../../components/webhooks/RotateSecretDialog';

import { useGetWebhookById } from '../../hooks/webhooks/useGetWebhookById';
import { useRotateWebhookSecret } from '../../hooks/webhooks/useRotateWebhookSecret';
import { useDeleteWebhook } from '../../hooks/webhooks/useDeleteWebhook';
import { LAST_STATUS_META, WEBHOOK_AVAILABLE_EVENTS } from '../../components/webhooks/webhookConstants';

dayjs.extend(relativeTime);

const labelForEvent = (code, isArabic) => {
    const meta = WEBHOOK_AVAILABLE_EVENTS.find((e) => e.value === code);
    if (!meta) return code;
    return isArabic ? meta.labelAr : meta.labelEn;
};

const WebhookDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const { webhook, isLoading, error } = useGetWebhookById(id);
    const { rotateSecretAsync, isRotating } = useRotateWebhookSecret();
    const { deleteWebhookMutate, isDeleting } = useDeleteWebhook();

    const [rotateOpen, setRotateOpen] = useState(false);
    const [newSecret, setNewSecret] = useState(null);
    const [copiedUrl, setCopiedUrl] = useState(false);

    const handleRotate = async () => {
        try {
            const res = await rotateSecretAsync(id);
            const secret = res?.data?.secret;
            setRotateOpen(false);
            if (secret) setNewSecret(secret);
        } catch {
            // toast handled by hook
        }
    };

    const handleDelete = () => {
        if (!window.confirm(isArabic
            ? 'هل أنت متأكد من حذف هذا الـ Webhook؟'
            : 'Are you sure you want to delete this webhook?')) {
            return;
        }
        deleteWebhookMutate(id, {
            onSuccess: () => navigate('/settings/integrations/webhooks'),
        });
    };

    const handleCopyUrl = async () => {
        if (!webhook?.url) return;
        try {
            await navigator.clipboard.writeText(webhook.url);
            setCopiedUrl(true);
            setTimeout(() => setCopiedUrl(false), 1500);
        } catch {
            /* noop */
        }
    };

    if (isLoading) {
        return (
            <WrapperSection>
                <GlobalBreadcrumbs />
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                </Box>
            </WrapperSection>
        );
    }

    if (error || !webhook) {
        return (
            <WrapperSection>
                <GlobalBreadcrumbs />
                <Alert severity="error">
                    {isArabic ? 'تعذر تحميل بيانات الـ Webhook' : 'Failed to load webhook'}
                </Alert>
            </WrapperSection>
        );
    }

    const statusMeta = webhook.last_status ? LAST_STATUS_META[webhook.last_status] : null;
    const events = Array.isArray(webhook.events) ? webhook.events : [];

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />

            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    mb: 3,
                }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={2}
                >
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="h5" fontWeight={700}>
                                {webhook.name}
                            </Typography>
                            <Chip
                                size="small"
                                label={webhook.is_active
                                    ? (isArabic ? 'مُفعّل' : 'Active')
                                    : (isArabic ? 'متوقف' : 'Inactive')}
                                color={webhook.is_active ? 'success' : 'default'}
                                variant="tonal"
                                sx={{ fontWeight: 'bold', borderRadius: '6px' }}
                            />
                            {statusMeta && (
                                <Chip
                                    size="small"
                                    label={isArabic ? statusMeta.labelAr : statusMeta.labelEn}
                                    color={statusMeta.color}
                                    variant="tonal"
                                    sx={{ fontWeight: 'bold', borderRadius: '6px' }}
                                />
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {webhook.last_fired_at
                                ? `${isArabic ? 'آخر تشغيل' : 'Last fired'} ${dayjs(webhook.last_fired_at).locale(isArabic ? 'ar' : 'en').fromNow()}`
                                : (isArabic ? 'لم يُشغّل بعد' : 'Never fired yet')}
                        </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/settings/integrations/webhooks/${id}/edit`)}
                        >
                            {isArabic ? 'تعديل' : 'Edit'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<AutorenewIcon />}
                            onClick={() => setRotateOpen(true)}
                        >
                            {isArabic ? 'تدوير السر' : 'Rotate secret'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutline />}
                            onClick={handleDelete}
                            disabled={isDeleting}
                        >
                            {isArabic ? 'حذف' : 'Delete'}
                        </Button>
                    </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Info grid */}
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {isArabic ? 'الرابط' : 'URL'}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                                flex: 1,
                            }}
                        >
                            {webhook.url}
                        </Typography>
                        <Tooltip title={copiedUrl ? (isArabic ? 'تم النسخ' : 'Copied') : (isArabic ? 'نسخ' : 'Copy')}>
                            <IconButton size="small" onClick={handleCopyUrl}>
                                <ContentCopy fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        {isArabic ? 'الأحداث المشترك بها' : 'Subscribed events'}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                        {events.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">
                                {isArabic ? 'لا توجد أحداث' : 'No events'}
                            </Typography>
                        ) : events.map((evt) => (
                            <Chip
                                key={evt}
                                label={labelForEvent(evt, isArabic)}
                                size="small"
                                color="info"
                                variant="tonal"
                                sx={{
                                    fontWeight: 'bold',
                                    borderRadius: '6px',
                                    '& .MuiChip-label': { fontFamily: 'monospace', fontSize: 11 },
                                }}
                            />
                        ))}
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {isArabic ? 'السر' : 'Secret'}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: 'monospace',
                            color: 'text.secondary',
                        }}
                    >
                        {webhook.secret_masked || '••••••••••••••••'}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                        {isArabic
                            ? 'لا يمكن استرجاع قيمة السر — استخدم "تدوير السر" لإصدار سر جديد.'
                            : 'The secret can\'t be revealed — use "Rotate secret" to issue a new one.'}
                    </Typography>
                </Box>
            </Paper>

            {/* Deliveries log */}
            <WebhookDeliveriesTable webhookId={id} />

            {/* Dialogs */}
            <RotateSecretDialog
                open={rotateOpen}
                onClose={() => setRotateOpen(false)}
                onConfirm={handleRotate}
                isRotating={isRotating}
            />

            <WebhookSecretDialog
                open={Boolean(newSecret)}
                secret={newSecret}
                onClose={() => setNewSecret(null)}
            />
        </WrapperSection>
    );
};

export default WebhookDetail;
